import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Usuario } from "../../../shared/tipos";
import { api, definirAoExpirarSessao, entrar as entrarApi, renovarSessao, sair as sairApi } from "../api/cliente";

interface ContextoAuth {
  usuario: Usuario | null;
  carregando: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  recarregar: () => Promise<void>;
}

const Contexto = createContext<ContextoAuth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [carregando, setCarregando] = useState(true);
  const queryClient = useQueryClient();

  const recarregar = useCallback(async () => {
    try {
      setUsuario(await api<Usuario>("/api/me"));
    } catch {
      setUsuario(null);
    }
  }, []);

  useEffect(() => {
    definirAoExpirarSessao(() => {
      setUsuario(null);
      queryClient.clear();
    });
    (async () => {
      if (await renovarSessao()) await recarregar();
      setCarregando(false);
    })();
  }, [queryClient, recarregar]);

  const entrar = useCallback(
    async (email: string, senha: string) => {
      await entrarApi(email, senha);
      await recarregar();
    },
    [recarregar],
  );

  const sair = useCallback(async () => {
    await sairApi();
    queryClient.clear();
    setUsuario(null);
  }, [queryClient]);

  const valor = useMemo(() => ({ usuario, carregando, entrar, sair, recarregar }), [usuario, carregando, entrar, sair, recarregar]);
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useAuth(): ContextoAuth {
  const ctx = useContext(Contexto);
  if (!ctx) throw new Error("useAuth fora do AuthProvider");
  return ctx;
}

export function podeEditar(usuario: Usuario | null): boolean {
  return Boolean(usuario && (usuario.papel === "admin" || usuario.pode_editar));
}
